import { Button } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { useIntl } from 'react-intl';
import { StyleSheet, Text, View } from 'react-native';

import { routes } from 'navigation/routing';
import { useProgressAnimation } from 'pages/loyalty/loyalty-profile/profile-card/components/hooks/use-progress-animation';
import type {
  AnimationState,
  ProgressData,
  Tier,
} from 'pages/loyalty/loyalty-profile/profile-card/components/hooks/use-progress-calculation';
import { primitive } from 'styles/constants/primitives';

import { GreetingSection } from './greeting-section';
import { ProgressBar } from './progress-bar';

interface ProgressSectionProps {
  userName: string;
  tiers: Tier[];
  currentPoints: number;
  lastSeenLoyaltyPoints: number;
  progressData: ProgressData;
  animationState: AnimationState;
}

export const ProgressSection = ({
  userName,
  tiers,
  currentPoints,
  lastSeenLoyaltyPoints,
  progressData,
  animationState,
}: ProgressSectionProps) => {
  const { navigate } = useRouter();
  const { formatMessage } = useIntl();

  const { displayedPoints, showConfettiAnimation, widthSV } = useProgressAnimation({
    currentPoints,
    lastSeenLoyaltyPoints,
    shouldAnimate: animationState.shouldAnimate,
    startValue: animationState.startValue,
    targetValue: animationState.targetValue,
  });

  if (!progressData) {
    return null;
  }

  const { pointsNeededForNextTier, currentTier } = progressData;

  return (
    <>
      <GreetingSection
        userName={userName}
        currentPoints={currentPoints}
        displayedPoints={displayedPoints}
      />

      <View style={styles.progressSection}>
        <ProgressBar
          currentPoints={currentPoints}
          firstTierPoints={tiers[0].points}
          tiers={tiers}
          widthSV={widthSV}
          showConfettiAnimation={showConfettiAnimation}
        />

        {currentPoints > 0 && currentTier > 0 ? (
          <Text style={[styles.progressLegend, { opacity: pointsNeededForNextTier > 0 ? 1 : 0 }]}>
            {formatMessage(
              {
                id: 'justMoreToReachNextLevel',
                defaultMessage: 'Just {points} more to reach the next level',
              },
              { points: pointsNeededForNextTier }
            )}
          </Text>
        ) : (
          <View style={styles.ctaContainer}>
            <Text style={styles.progressLegend}>
              {formatMessage({
                id: 'placeAnOrderToEarn',
                defaultMessage: 'Place an order to earn',
              })}
            </Text>
            <Button variant="outline" size="sm" onPress={() => navigate(routes.menu)}>
              {formatMessage({ id: 'startNow', defaultMessage: 'Start Now' })}
            </Button>
          </View>
        )}
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  progressSection: { marginTop: 14 },
  progressLegend: {
    fontFamily: primitive.fontFamily.$body,
    fontSize: primitive.fontSizes.xs,
    color: primitive.bk.$bbqBrown,
  },
  ctaContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: primitive.spacing.$4,
  },
});
